import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = ({ onSubmit, error: externalError, networkError }) => {
  const { t, currentLanguage } = useLanguage();
  const { signInDemo, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = t('login.emailRequired');
    }
    if (!formData?.password) {
      newErrors.password = t('login.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await onSubmit(formData);
    } catch (error) {
      // Parent (login/index.jsx) handles errors via the `error` prop
      // This catch is a safety net only
      setErrors({ submit: error?.message || t('login.invalidCredentials') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = () => {
    signInDemo();
    navigate('/agency-dashboard');
  };

  const handleForgotPassword = async () => {
    if (!formData?.email) {
      setErrors(prev => ({
        ...prev,
        email: currentLanguage === 'fr' ? 'Entrez votre email pour réinitialiser le mot de passe' : 'أدخل بريدك الإلكتروني لإعادة تعيين كلمة المرور'
      }));
      return;
    }
    setResetLoading(true);
    setResetMsg('');
    try {
      await resetPassword(formData?.email);
      setResetMsg(currentLanguage === 'fr' ?'✅ Email de réinitialisation envoyé. Vérifiez votre boîte mail.' :'✅ تم إرسال رابط إعادة التعيين. تحقق من بريدك الإلكتروني.');
    } catch (err) {
      setResetMsg(currentLanguage === 'fr'
        ? `Erreur: ${err?.message || 'Impossible d\'envoyer l\'email'}`
        : `خطأ: ${err?.message || 'تعذر إرسال البريد الإلكتروني'}`);
    } finally {
      setResetLoading(false);
    }
  };

  const displayError = errors?.submit || externalError;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          {t('login.title')}
        </h1>
        <p className="text-muted-foreground">
          {t('login.subtitle')}
        </p>
      </div>
      <Input
        label={t('login.email')}
        type="email"
        placeholder={t('login.emailPlaceholder')}
        value={formData?.email}
        onChange={(e) => handleInputChange('email', e?.target?.value)}
        error={errors?.email}
        required
      />
      <div className="relative">
        <Input
          label={t('login.password')}
          type={showPassword ? 'text' : 'password'}
          placeholder={t('login.passwordPlaceholder')}
          value={formData?.password}
          onChange={(e) => handleInputChange('password', e?.target?.value)}
          error={errors?.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>
      <div className="flex items-center justify-between">
        <Checkbox
          label={t('login.rememberMe')}
          checked={formData?.rememberMe}
          onChange={(e) => handleInputChange('rememberMe', e?.target?.checked)}
        />
        <button
          type="button"
          className="text-sm text-primary hover:text-primary/80 transition-smooth disabled:opacity-50"
          onClick={handleForgotPassword}
          disabled={resetLoading}
        >
          {resetLoading
            ? (currentLanguage === 'fr' ? 'Envoi...' : 'جاري الإرسال...')
            : t('login.forgotPassword')}
        </button>
      </div>
      {resetMsg && (
        <div className={`p-3 rounded-md text-sm ${resetMsg?.startsWith('✅') ? 'bg-success/10 border border-success/20 text-success' : 'bg-destructive/10 border border-destructive/20 text-destructive'}`}>
          {resetMsg}
        </div>
      )}
      {displayError && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2">
          <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0" />
          <p className="text-sm text-destructive">{displayError}</p>
        </div>
      )}
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        iconName="LogIn"
        iconPosition="right"
      >
        {t('login.signIn')}
      </Button>
      <div className="relative flex items-center gap-3 my-2">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {currentLanguage === 'fr' ? 'ou' : 'أو'}
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <button
        type="button"
        onClick={handleDemoLogin}
        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all duration-200 ${
          networkError
            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90 shadow-md animate-pulse'
            : 'border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary hover:border-primary/60'
        }`}
      >
        <Icon name="PlayCircle" size={18} />
        {currentLanguage === 'fr' ? 'Accéder à la démo — Tableau de bord agence' : 'تجربة العرض التوضيحي — لوحة تحكم الوكالة'}
      </button>
      <div className="text-center pt-4 border-t border-border">
        <p className="text-sm text-muted-foreground">
          {t('login.noAccount')}{' '}
          <button
            onClick={() => navigate('/agency-registration')}
            className="text-primary hover:text-primary/80 font-medium transition-smooth"
          >
            {t('login.register')}
          </button>
        </p>
      </div>
    </form>
  );
};

export default LoginForm;